import { createClient } from "./http";
import { env } from "../config/env";

const api = createClient(env.WAREHOUSE_INVENTORY_BASE_URL);

export const WarehouseNotificationsService = {
  async list(params = {}) {
    return api.get("/api/warehouse/notifications", {
      params: {
        page: 1,
        limit: 20,
        ...params,
      },
    });
  },

  async unreadCount() {
    return api.get("/api/warehouse/notifications/unread-count");
  },

  async markRead(id) {
    return api.patch(`/api/warehouse/notifications/${id}/read`);
  },

  async markAllRead() {
    return api.patch("/api/warehouse/notifications/read-all");
  },
};

const ADMIN_ROOT = "/admin/dashboard";

function clean(value) {
  return String(value || "").trim();
}

function safeLower(value) {
  return clean(value).toLowerCase();
}

function getActionUrl(item = {}) {
  return clean(
    item?.actionUrl ||
      item?.action_url ||
      item?.url ||
      item?.link ||
      item?.path ||
      item?.redirectUrl ||
      item?.redirect_url ||
      item?.deepLink ||
      item?.deep_link ||
      item?.meta?.actionUrl ||
      item?.meta?.action_url ||
      item?.meta?.url ||
      item?.meta?.link ||
      item?.meta?.path ||
      ""
  );
}

function normalizePath(rawUrl) {
  const raw = clean(rawUrl);
  if (!raw) return "";
  if (raw.startsWith("/")) return raw;

  try {
    const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsed = new URL(raw, base);
    return `${parsed.pathname}${parsed.search || ""}${parsed.hash || ""}`;
  } catch (_err) {
    return raw;
  }
}

function route(path) {
  return `${ADMIN_ROOT}${path}`;
}

function buildSearchKey(item = {}, actionPath = "") {
  const meta = item?.meta && typeof item.meta === "object" ? item.meta : {};

  return [
    actionPath,
    item?.type,
    item?.event_type,
    item?.eventType,
    item?.title,
    item?.message,
    item?.body,
    item?.description,
    meta?.type,
    meta?.event_type,
    meta?.eventType,
    meta?.entityType,
    meta?.entity_type,
    meta?.module,
    meta?.action,
    meta?.displayAction,
    meta?.orderId,
    meta?.order_id,
    meta?.warehouseId,
    meta?.warehouse_id,
  ]
    .map((value) => (typeof value === "object" ? JSON.stringify(value) : clean(value)))
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function mapKnownFrontendRoute(actionPath) {
  const path = normalizePath(actionPath);
  const lowerPath = safeLower(path);

  if (!path) return "";
  if (lowerPath.startsWith(`${ADMIN_ROOT}/`) || lowerPath === ADMIN_ROOT) return path;
  if (lowerPath.startsWith("/dashboard/")) return `/admin${path}`;
  if (lowerPath.startsWith("/warehouse/customer-orders")) return route("/customer-orders");
  if (lowerPath.startsWith("/warehouse/inbound-receipts")) return route("/inbound-receipts");
  if (lowerPath.startsWith("/warehouse/stock")) return route("/stock");
  if (lowerPath.startsWith("/warehouse/inventory")) return route("/inventory");
  if (lowerPath.startsWith("/warehouse/products")) return route("/products");
  if (lowerPath.startsWith("/warehouse/activity-logs")) return route("/activity-logs");

  return "";
}

function mapApiUrlToRoute(actionPath) {
  const path = normalizePath(actionPath);
  const lowerPath = safeLower(path);

  if (!lowerPath) return "";

  if (
    lowerPath.includes("/warehouse/customer-orders") ||
    lowerPath.includes("/api/warehouse/customer-orders") ||
    lowerPath.includes("/api/warehouse/orders") ||
    lowerPath.includes("/api/orders") ||
    lowerPath.includes("/orders/")
  ) {
    return route("/customer-orders");
  }

  if (lowerPath.includes("/warehouse/inbound-receipts") || lowerPath.includes("/api/warehouse/inbound")) {
    return route("/inbound-receipts");
  }

  if (lowerPath.includes("/warehouse/stock") || lowerPath.includes("/api/warehouse/stock")) {
    return route("/stock");
  }

  if (lowerPath.includes("/api/warehouse/inventory") || lowerPath.includes("/warehouse/inventory")) {
    return route("/inventory");
  }

  if (lowerPath.includes("/api/warehouse/products") || lowerPath.includes("/warehouse/products")) {
    return route("/products");
  }

  if (lowerPath.includes("/api/warehouse/activity") || lowerPath.includes("/warehouse/activity")) {
    return route("/activity-logs");
  }

  return "";
}

function mapTextToRoute(key) {
  const text = safeLower(key);
  if (!text) return "";

  if (
    text.includes("warehouse order") ||
    text.includes("customer order") ||
    text.includes("order created") ||
    text.includes("order assigned") ||
    text.includes("order cancelled") ||
    text.includes("order delivered") ||
    text.includes("dispatch") ||
    /\bwo[-_\s]?\d+/i.test(text)
  ) {
    return route("/customer-orders");
  }

  if (text.includes("inbound") || text.includes("receipt") || text.includes("putaway") || text.includes("grn")) {
    return route("/inbound-receipts");
  }

  if (text.includes("low stock") || text.includes("stock") || text.includes("expiry") || text.includes("expired")) {
    return route("/stock");
  }

  if (text.includes("inventory") || text.includes("batch")) return route("/inventory");
  if (text.includes("product") || text.includes("catalog")) return route("/products");
  if (text.includes("employee")) return route("/employees");
  if (text.includes("activity") || text.includes("log")) return route("/activity-logs");

  return "";
}

function routeWithoutQuery(path) {
  return clean(normalizePath(path)).split("?")[0].split("#")[0].toLowerCase();
}

export function isWarehouseNotificationRoute(mappedRoute = "") {
  const lowerRoute = routeWithoutQuery(mappedRoute);

  return (
    lowerRoute === `${ADMIN_ROOT}/customer-orders` ||
    lowerRoute.startsWith(`${ADMIN_ROOT}/customer-orders/`) ||
    lowerRoute === `${ADMIN_ROOT}/inbound-receipts` ||
    lowerRoute.startsWith(`${ADMIN_ROOT}/inbound-receipts/`) ||
    lowerRoute === `${ADMIN_ROOT}/stock` ||
    lowerRoute.startsWith(`${ADMIN_ROOT}/stock/`) ||
    lowerRoute === `${ADMIN_ROOT}/inventory` ||
    lowerRoute.startsWith(`${ADMIN_ROOT}/inventory/`) ||
    lowerRoute === `${ADMIN_ROOT}/products` ||
    lowerRoute.startsWith(`${ADMIN_ROOT}/products/`) ||
    lowerRoute === `${ADMIN_ROOT}/activity-logs`
  );
}

export function mapWarehouseNotificationToRoute(item) {
  const actionPath = normalizePath(getActionUrl(item));
  const key = buildSearchKey(item, actionPath);

  return (
    mapKnownFrontendRoute(actionPath) ||
    mapApiUrlToRoute(actionPath) ||
    mapTextToRoute(key) ||
    route("/customer-orders")
  );
}
